/**
 * ServicePlanModal — create/edit a `service_plans` record.
 *
 * GST BASIS: the two CUSTOMER-FACING prices (sell price, setup fee) are typed
 * INC GST — that is the number the office quotes and the customer pays. They
 * are STORED ex-GST (`sellPrice` / `setupFee`), because every downstream
 * consumer — Utilmate, Xero, quote line items, MRR — reads the ex-GST basis.
 *
 * Cost price stays EX GST on purpose: it is the wholesale figure off the
 * supplier's invoice, and margin is computed cost-vs-sell on the same ex-GST
 * basis, so both sides of that division must stay ex-GST.
 */
import { useState } from 'react';
import type { ServicePlan } from '@/types/schema';
import {
  createServicePlan,
  updateServicePlan,
  calculateMargin,
} from '@/services/service-plans.service';
import { useTenant } from '@/hooks/useTenant';
import { getTenantTaxRate } from '@/utils/taxRate';
import { exGstToIncString, incStringToExGst } from '@/components/common/IncGstPriceInput';
import { formatCurrency } from '@/utils/pricing';

const SERVICE_TYPE_OPTIONS: ServicePlan['serviceType'][] = [
  'NBN',
  'OptiComm',
  'Starlink',
  '4G Mobile',
  '4G Data',
  'VoIP',
  'Other',
];

export interface ServicePlanModalProps {
  plan: ServicePlan | null;
  onClose: () => void;
  onSuccess: (savedPlan?: ServicePlan) => void;
  userId: string;
  userName: string;
  tenantId: string;
  defaultServiceType?: ServicePlan['serviceType'];
}

export function ServicePlanModal({
  plan,
  onClose,
  onSuccess,
  userId,
  userName,
  tenantId,
  defaultServiceType,
}: ServicePlanModalProps) {
  const { tenant } = useTenant();
  const taxRate = getTenantTaxRate(tenant);

  const [formData, setFormData] = useState<Partial<ServicePlan>>(
    plan || {
      serviceType: defaultServiceType || 'NBN',
      planName: '',
      displayName: '',
      sku: '',
      planTier: '',
      downloadSpeed: '',
      uploadSpeed: '',
      dataAllowance: '',
      features: [],
      costPrice: 0,
      sellPrice: 0,
      setupFee: 0,
      margin: 0,
      supplier: '',
      supplierPlanCode: '',
      isActive: true,
      isPromoted: false,
      sortOrder: 999,
    }
  );

  const [featureInput, setFeatureInput] = useState('');

  // Inc-GST entry strings for the two sell-side prices. Held as raw strings so
  // typing isn't fought by the inc→ex→inc round-trip; `formData` keeps the
  // ex-GST numbers that actually get stored.
  const [sellPriceInc, setSellPriceInc] = useState<string>(() =>
    exGstToIncString(plan?.sellPrice ?? 0, taxRate)
  );
  const [setupFeeInc, setSetupFeeInc] = useState<string>(() =>
    plan?.setupFee ? exGstToIncString(plan.setupFee, taxRate) : ''
  );

  const handleChange = (field: keyof ServicePlan, value: unknown) => {
    const updates: Partial<ServicePlan> = { [field]: value } as Partial<ServicePlan>;

    if (field === 'costPrice' || field === 'sellPrice') {
      const costPrice = field === 'costPrice' ? (value as number) : formData.costPrice || 0;
      const sellPrice = field === 'sellPrice' ? (value as number) : formData.sellPrice || 0;
      updates.margin = calculateMargin(costPrice, sellPrice);
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  /** Inc-GST input → ex-GST number on formData (0 when blank/unparseable). */
  const handleIncPriceChange = (
    field: 'sellPrice' | 'setupFee',
    incStr: string,
  ) => {
    if (field === 'sellPrice') setSellPriceInc(incStr);
    else setSetupFeeInc(incStr);
    const ex = incStringToExGst(incStr, taxRate);
    handleChange(field, Number.isNaN(ex) ? 0 : ex);
  };

  const handleAddFeature = () => {
    if (!featureInput.trim()) return;
    setFormData((prev) => ({
      ...prev,
      features: [...(prev.features || []), featureInput.trim()],
    }));
    setFeatureInput('');
  };

  const handleRemoveFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.planName || !formData.displayName) {
      alert('Plan name and display name are required');
      return;
    }

    if (formData.costPrice === undefined || formData.sellPrice === undefined) {
      alert('Cost price and sell price are required');
      return;
    }

    if (formData.sellPrice < formData.costPrice) {
      if (!confirm('Sell price is less than cost price (negative margin).\n\nAre you sure you want to continue?')) {
        return;
      }
    }

    try {
      if (plan?.id) {
        await updateServicePlan(plan.id, formData);
        onSuccess({ ...plan, ...formData } as ServicePlan);
      } else {
        const id = await createServicePlan(
          { ...formData, tenantId } as Omit<ServicePlan, 'id' | 'createdAt' | 'updatedAt'>,
          userId,
          userName
        );
        onSuccess({ ...formData, id, tenantId } as ServicePlan);
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Error saving plan');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-bold">{plan ? 'Edit Plan' : 'Add New Plan'}</h2>
          <p className="text-sm text-blue-100 mt-1">
            {plan ? 'Update service plan details' : 'Create a new service plan offering'}
          </p>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.serviceType}
                  onChange={(e) => handleChange('serviceType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SERVICE_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Tier</label>
                <input
                  type="text"
                  value={formData.planTier || ''}
                  onChange={(e) => handleChange('planTier', e.target.value)}
                  placeholder="e.g., Basic, Standard, Premium"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Plan Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.planName || ''}
                  onChange={(e) => handleChange('planName', e.target.value)}
                  placeholder="e.g., NBN 100/40 Unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName || ''}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  placeholder="e.g., NBN Fast"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU / Product Code</label>
                <input
                  type="text"
                  value={formData.sku || ''}
                  onChange={(e) => handleChange('sku', e.target.value)}
                  placeholder="e.g., NBN-FAST-100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder || 999}
                  onChange={(e) => handleChange('sortOrder', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Specifications</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Download Speed</label>
                <input
                  type="text"
                  value={formData.downloadSpeed || ''}
                  onChange={(e) => handleChange('downloadSpeed', e.target.value)}
                  placeholder="e.g., 100 Mbps"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Speed</label>
                <input
                  type="text"
                  value={formData.uploadSpeed || ''}
                  onChange={(e) => handleChange('uploadSpeed', e.target.value)}
                  placeholder="e.g., 40 Mbps"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Allowance</label>
                <input
                  type="text"
                  value={formData.dataAllowance || ''}
                  onChange={(e) => handleChange('dataAllowance', e.target.value)}
                  placeholder="e.g., Unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">
              Pricing
              <span className="text-xs font-normal text-gray-500 ml-2">
                Sell price + setup fee are inc GST · cost price is ex GST (supplier basis)
              </span>
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Price (ex GST) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costPrice || ''}
                    onChange={(e) => handleChange('costPrice', parseFloat(e.target.value))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sell Price (inc GST) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={sellPriceInc}
                    onChange={(e) => handleIncPriceChange('sellPrice', e.target.value)}
                    aria-label="Sell price including GST"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {!!formData.sellPrice && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Billed as {formatCurrency(formData.sellPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ex GST
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Setup Fee (inc GST)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={setupFeeInc}
                    onChange={(e) => handleIncPriceChange('setupFee', e.target.value)}
                    aria-label="Setup fee including GST"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {!!formData.setupFee && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Billed as {formatCurrency(formData.setupFee, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ex GST
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Margin</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.margin || ''}
                    readOnly
                    className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Supplier */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Supplier Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name</label>
                <input
                  type="text"
                  value={formData.supplier || ''}
                  onChange={(e) => handleChange('supplier', e.target.value)}
                  placeholder="e.g., NBN Co, Telstra Wholesale"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Plan Code</label>
                <input
                  type="text"
                  value={formData.supplierPlanCode || ''}
                  onChange={(e) => handleChange('supplierPlanCode', e.target.value)}
                  placeholder="e.g., NBN100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Features</h3>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddFeature()}
                placeholder="Add feature (press Enter)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddFeature}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(formData.features || []).map((feature, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2"
                >
                  {feature}
                  <button onClick={() => handleRemoveFeature(index)} className="hover:text-blue-900">
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Status Toggles */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Status</h3>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Active (available for new customers)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isPromoted}
                  onChange={(e) => handleChange('isPromoted', e.target.checked)}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="text-sm text-gray-700">Promoted (featured plan)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {plan ? 'Update Plan' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
